import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Building2, Receipt, DollarSign, Briefcase, Megaphone, Mail, MapPin, Send,
    CheckCircle2, XCircle, AlertCircle, ExternalLink, Sparkles,
    Users, Search, Star, Trash2, Radio, Zap, PauseCircle, Gift, BarChart3, Scale,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { US_STATES } from "@/data/pillars";
import AnalyticsPanel from "@/components/admin/AnalyticsPanel";
import CompliancePanel from "@/components/admin/CompliancePanel";

const TABS = [
    { key: "b2b", label: "B2B accounts", icon: Building2 },
    { key: "tx", label: "Transactions", icon: Receipt },
    { key: "due", label: "Payments due", icon: DollarSign },
    { key: "jobs", label: "Jobs", icon: Briefcase },
    { key: "broadcasts", label: "Shift blasts", icon: Radio },
    { key: "pools", label: "Labor pools", icon: MapPin },
    { key: "apollo", label: "Apollo leads", icon: Users },
    { key: "referrals", label: "Referrals", icon: Gift },
    { key: "marketing", label: "Marketing", icon: Megaphone },
    { key: "comms", label: "Communications", icon: Mail },
    { key: "analytics", label: "Analytics", icon: BarChart3 },
    { key: "compliance", label: "Compliance", icon: Scale },
];

const fmt$ = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function AdminOperationsPanel({ isFinancialViewer = true }) {
    const [tab, setTab] = useState("b2b");
    return (
        <Card className="rounded-2xl border-stone-200">
            <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <h2 className="font-outfit text-xl font-semibold text-stone-900">
                        Operations Center
                    </h2>
                    <span className="text-xs text-stone-500 font-dmsans">
                        Deep visibility across accounts, money, jobs, marketing & delivery
                    </span>
                </div>
                <div className="flex flex-wrap gap-1 mb-5 border-b border-stone-200 pb-3">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        const active = tab === t.key;
                        return (
                            <button
                                key={t.key}
                                data-testid={`ops-tab-${t.key}`}
                                onClick={() => setTab(t.key)}
                                className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-outfit font-medium transition-all ${
                                    active
                                        ? "bg-stone-900 text-white"
                                        : "bg-stone-100 text-stone-700 hover:bg-stone-200"
                                }`}
                            >
                                <Icon className="w-3.5 h-3.5" /> {t.label}
                            </button>
                        );
                    })}
                </div>
                {tab === "b2b" && <B2BPanel />}
                {tab === "tx" && <TransactionsPanel />}
                {tab === "due" && (isFinancialViewer ? <PaymentsDuePanel /> : <FinancialLockedPanel />)}
                {tab === "jobs" && <JobsPanel />}
                {tab === "broadcasts" && <BroadcastsPanel />}
                {tab === "pools" && <LaborPoolsPanel />}
                {tab === "apollo" && <ApolloPanel />}
                {tab === "referrals" && <ReferralsPanel />}
                {tab === "marketing" && <MarketingPanel />}
                {tab === "comms" && <CommsPanel />}
                {tab === "analytics" && <AnalyticsPanel />}
                {tab === "compliance" && <CompliancePanel />}
            </CardContent>
        </Card>
    );
}

function FinancialLockedPanel() {
    return (
        <div className="p-8 text-center rounded-xl bg-stone-50 border border-stone-200">
            <DollarSign className="w-6 h-6 mx-auto text-stone-400 mb-2" />
            <div className="font-outfit font-semibold text-stone-700">Financials are executive-only</div>
            <div className="text-xs text-stone-500 font-dmsans mt-1">
                Only super_admin, admin, and COO roles can view payments due.
            </div>
        </div>
    );
}

function B2BPanel() {
    const [rows, setRows] = useState([]);
    useEffect(() => { api.get("/admin/b2b-accounts").then((r) => setRows(r.data)); }, []);
    return (
        <div className="space-y-2">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-stone-500 grid grid-cols-12 gap-3 pb-2 border-b border-stone-100">
                <div className="col-span-5">Agency</div>
                <div className="col-span-2">EIN</div>
                <div className="col-span-2">Stripe</div>
                <div className="col-span-1">Shifts</div>
                <div className="col-span-2 text-right">Lifetime</div>
            </div>
            {rows.length === 0 && <div className="text-sm text-stone-500 py-6 text-center font-dmsans">No agencies yet.</div>}
            {rows.map((a) => (
                <div key={a.id} data-testid="ops-b2b-row" className="grid grid-cols-12 gap-3 py-2 border-b border-stone-100 last:border-b-0 items-center">
                    <div className="col-span-5 min-w-0">
                        <div className="font-outfit font-semibold text-sm text-stone-900 truncate">{a.agency_name}</div>
                        <div className="text-xs text-stone-500 font-dmsans truncate">{a.email}</div>
                    </div>
                    <div className="col-span-2 text-xs font-mono text-stone-600 truncate">{a.ein || "—"}</div>
                    <div className="col-span-2">
                        {a.stripe_onboarded
                            ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50 text-[10px]">Onboarded</Badge>
                            : <Badge className="bg-amber-50 text-amber-700 border-amber-100 hover:bg-amber-50 text-[10px]">Pending</Badge>}
                    </div>
                    <div className="col-span-1 text-xs text-stone-700 font-dmsans">
                        {a.shifts.open}·{a.shifts.claimed}·{a.shifts.completed}
                    </div>
                    <div className="col-span-2 text-right font-outfit font-semibold text-sm">{fmt$(a.lifetime_gmv_cents)}</div>
                </div>
            ))}
        </div>
    );
}

function TransactionsPanel() {
    const [mode, setMode] = useState("home-health");
    const [rows, setRows] = useState([]);
    useEffect(() => {
        api.get(`/admin/transactions/${mode}?limit=100`).then((r) => setRows(r.data));
    }, [mode]);
    return (
        <div>
            <div className="flex gap-1 mb-3">
                <button data-testid="ops-tx-mode-hh" onClick={() => setMode("home-health")}
                    className={`px-3 h-7 rounded-full text-[11px] font-outfit font-medium ${mode === "home-health" ? "bg-emerald-700 text-white" : "bg-stone-100 text-stone-700"}`}>
                    Home Health
                </button>
                <button data-testid="ops-tx-mode-b2b" onClick={() => setMode("b2b")}
                    className={`px-3 h-7 rounded-full text-[11px] font-outfit font-medium ${mode === "b2b" ? "bg-sky-700 text-white" : "bg-stone-100 text-stone-700"}`}>
                    B2B / Shifts
                </button>
            </div>
            <div className="max-h-96 overflow-y-auto space-y-1">
                {rows.map((r) => (
                    <div key={r.id} data-testid="ops-tx-row" className="text-xs p-2.5 border border-stone-100 rounded-lg flex items-center justify-between gap-3 font-dmsans">
                        <div className="min-w-0 flex-1">
                            <div className="font-medium text-stone-900 truncate">
                                {mode === "home-health"
                                    ? (r.package_label ? `${r.package_label} package` : `${r.services_count} services · à la carte`)
                                    : r.title}
                            </div>
                            <div className="text-stone-500 text-[11px] truncate">
                                {r.state && `${r.state} · `}
                                {mode === "home-health" && r.hours_per_week && `${r.hours_per_week}h/wk · `}
                                {r.status}
                            </div>
                        </div>
                        <div className="font-outfit font-semibold text-stone-800 shrink-0">
                            {fmt$(r.weekly_total_cents || r.hourly_rate_cents)}
                        </div>
                    </div>
                ))}
                {rows.length === 0 && <div className="text-sm text-stone-500 py-6 text-center font-dmsans">No transactions yet.</div>}
            </div>
        </div>
    );
}

function PaymentsDuePanel() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/admin/payments-due").then((r) => setData(r.data)); }, []);
    if (!data) return <div className="text-sm text-stone-500 py-6 text-center font-dmsans">Loading…</div>;
    return (
        <div className="grid md:grid-cols-2 gap-4">
            <DueColumn
                title="Family invoices due (weekly)"
                icon={<AlertCircle className="w-4 h-4 text-amber-700" />}
                total={data.family_due.total_cents}
                count={data.family_due.count}
                accent="amber"
            >
                {data.family_due.lines.slice(0, 15).map((l) => (
                    <div key={l.care_request_id} className="text-xs border border-stone-100 rounded-lg p-2 flex justify-between items-center gap-2">
                        <div className="min-w-0">
                            <div className="font-medium text-stone-900 truncate">
                                {l.family_name} <span className="text-stone-400">· {l.care_recipient}</span>
                            </div>
                            <div className="text-stone-500 truncate">
                                {l.state} · {l.package_label || "à la carte"}
                                {!l.payment_method_on_file && <span className="ml-1 text-red-700 font-medium">· No card on file</span>}
                            </div>
                        </div>
                        <div className="font-outfit font-semibold text-emerald-800 shrink-0">{fmt$(l.weekly_amount_cents)}</div>
                    </div>
                ))}
            </DueColumn>
            <DueColumn
                title="Agency payouts pending"
                icon={<Sparkles className="w-4 h-4 text-sky-700" />}
                total={data.agency_due.total_cents}
                count={data.agency_due.count}
                accent="sky"
            >
                {data.agency_due.lines.slice(0, 15).map((l) => (
                    <div key={l.shift_id} className="text-xs border border-stone-100 rounded-lg p-2 flex justify-between items-center gap-2">
                        <div className="min-w-0">
                            <div className="font-medium text-stone-900 truncate">{l.agency_name}</div>
                            <div className="text-stone-500 truncate">Cadence: {l.cadence}</div>
                        </div>
                        <div className="font-outfit font-semibold text-sky-800 shrink-0">{fmt$(l.amount_cents)}</div>
                    </div>
                ))}
                {data.agency_due.lines.length === 0 && <div className="text-xs text-stone-400 font-dmsans">Nothing pending.</div>}
            </DueColumn>
        </div>
    );
}

function DueColumn({ title, icon, total, count, accent, children }) {
    const bg = { amber: "bg-amber-50", sky: "bg-sky-50" }[accent];
    return (
        <div className={`rounded-2xl p-4 ${bg} border border-stone-200`}>
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    {icon}
                    <div>
                        <div className="font-outfit font-semibold text-stone-900 text-sm">{title}</div>
                        <div className="text-[10px] text-stone-500 font-dmsans">{count} account{count !== 1 ? "s" : ""}</div>
                    </div>
                </div>
                <div className="font-outfit text-xl font-semibold text-stone-900">{fmt$(total)}</div>
            </div>
            <div className="space-y-1.5 max-h-80 overflow-y-auto">{children}</div>
        </div>
    );
}

function JobsPanel() {
    const [busy, setBusy] = useState(false);
    async function closeById() {
        const id = window.prompt("Shift ID to close?");
        if (!id) return;
        setBusy(true);
        try { await api.post(`/admin/shifts/${id}/close`); toast.success("Shift closed"); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    }
    return (
        <div className="space-y-3">
            <div className="rounded-xl border border-stone-200 bg-white p-4">
                <div className="font-outfit font-semibold text-stone-900 text-sm mb-1">Post a job (as admin)</div>
                <div className="text-xs text-stone-500 font-dmsans mb-3">
                    Admin-posted shifts are attributed to the chosen agency. Use the standard &ldquo;Post a shift&rdquo; flow
                    on the Shifts page &mdash; I&rsquo;ll add an inline form here in the next pass.
                </div>
                <Button
                    data-testid="ops-jobs-goto-shifts"
                    variant="outline"
                    className="rounded-full h-9"
                    onClick={() => (window.location.href = "/shifts")}
                >
                    Go to Shifts <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                </Button>
            </div>
            <div className="rounded-xl border border-stone-200 bg-white p-4">
                <div className="font-outfit font-semibold text-stone-900 text-sm mb-1">Close any shift</div>
                <div className="text-xs text-stone-500 font-dmsans mb-3">
                    Cancels the shift (status → cancelled). Audited.
                </div>
                <Button
                    data-testid="ops-jobs-close"
                    onClick={closeById}
                    disabled={busy}
                    className="rounded-full h-9 bg-red-700 hover:bg-red-800 text-white"
                >
                    Close a shift by ID
                </Button>
            </div>
        </div>
    );
}

function MarketingPanel() {
    const [apollo, setApollo] = useState(null);
    const [campaigns, setCampaigns] = useState([]);
    useEffect(() => {
        api.get("/admin/marketing/apollo/status").then((r) => setApollo(r.data));
        api.get("/admin/marketing/campaigns").then((r) => setCampaigns(r.data));
    }, []);
    async function newCampaign() {
        const name = window.prompt("Campaign name?");
        if (!name) return;
        const channel = window.prompt("Channel? (email / sms / apollo / social / other)", "email");
        if (!channel) return;
        try {
            await api.post("/admin/marketing/campaigns", { name, channel, audience: "all", body: "", status: "draft" });
            toast.success("Campaign saved as draft");
            const { data } = await api.get("/admin/marketing/campaigns");
            setCampaigns(data);
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    }
    return (
        <div className="space-y-4">
            <div className={`rounded-xl p-4 border ${apollo?.connected ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-1">
                            Apollo AI · Infini HealthCare
                        </div>
                        <div className="font-outfit font-semibold text-stone-900 text-sm flex items-center gap-2">
                            {apollo?.connected
                                ? (<><CheckCircle2 className="w-4 h-4 text-emerald-700" /> Connected</>)
                                : (<><AlertCircle className="w-4 h-4 text-amber-700" /> Not connected</>)}
                        </div>
                        <div className="text-xs text-stone-600 font-dmsans mt-1 max-w-xl">
                            {apollo?.note || "Apollo campaigns will run under your Infini HealthCare org."}
                        </div>
                    </div>
                    {!apollo?.connected && (
                        <Button
                            data-testid="ops-apollo-connect"
                            onClick={() => window.open("https://app.apollo.io/#/settings/integrations/api", "_blank")}
                            className="rounded-full bg-stone-900 hover:bg-stone-800 text-white h-9"
                        >
                            Get an API key <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                        </Button>
                    )}
                </div>
            </div>
            <div className="flex items-center justify-between">
                <div className="font-outfit font-semibold text-sm text-stone-900">Campaigns ({campaigns.length})</div>
                <Button data-testid="ops-campaign-new" onClick={newCampaign} className="rounded-full h-8 bg-emerald-700 hover:bg-emerald-800 text-white text-xs">
                    New campaign
                </Button>
            </div>
            <div className="space-y-1">
                {campaigns.length === 0 && <div className="text-xs text-stone-500 font-dmsans">No campaigns yet.</div>}
                {campaigns.map((c) => (
                    <div key={c.id} data-testid="ops-campaign-row" className="text-xs p-2.5 border border-stone-100 rounded-lg flex items-center justify-between gap-2 font-dmsans">
                        <div>
                            <div className="font-medium text-stone-900">{c.name}</div>
                            <div className="text-stone-500">{c.channel} · {c.audience} · {c.status}</div>
                        </div>
                        <div className="text-stone-500 text-[10px]">
                            sent {c.sent_count} · opened {c.opened_count} · clicked {c.clicked_count}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function CommsPanel() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/admin/comms/stats").then((r) => setData(r.data)); }, []);
    if (!data) return <div className="text-sm text-stone-500 py-6 text-center font-dmsans">Loading…</div>;
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <MiniStat label="Sent (30d)" value={data.totals.sent} icon={Mail} accent="stone" />
                <MiniStat label="Email OK" value={data.totals.email_ok} icon={CheckCircle2} accent="emerald" />
                <MiniStat label="Email failed" value={data.totals.email_failed} icon={XCircle} accent="red" />
                <MiniStat label="SMS OK / failed" value={`${data.totals.sms_ok} / ${data.totals.sms_failed}`} icon={AlertCircle} accent="sky" />
            </div>
            <div>
                <div className="font-outfit font-semibold text-sm text-stone-900 mb-2">Recent failures / bounces</div>
                {data.recent_failures.length === 0 ? (
                    <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg p-3 font-dmsans">
                        No delivery failures in the last 30 days. 🎉
                    </div>
                ) : (
                    <div className="space-y-1 max-h-72 overflow-y-auto">
                        {data.recent_failures.map((f) => (
                            <div key={f.id} data-testid="ops-comms-failure" className="text-xs border border-red-100 bg-red-50 rounded-lg p-2 flex justify-between items-center gap-2">
                                <div className="min-w-0">
                                    <div className="font-medium text-stone-900 truncate">
                                        {f.channel} · {f.to || "?"} <span className="text-red-700">({f.status})</span>
                                    </div>
                                    <div className="text-stone-500 truncate">{f.subject || f.error || "—"}</div>
                                </div>
                                <div className="text-stone-400 text-[10px] shrink-0">{new Date(f.created_at).toLocaleDateString()}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function MiniStat({ label, value, icon: Icon, accent }) {
    const fg = { emerald: "text-emerald-700", red: "text-red-700", sky: "text-sky-700", stone: "text-stone-700" }[accent];
    return (
        <div className="rounded-xl border border-stone-200 bg-white p-3">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-stone-500">
                <Icon className={`w-3.5 h-3.5 ${fg}`} /> {label}
            </div>
            <div className="font-outfit text-xl font-semibold text-stone-900 mt-1">{value}</div>
        </div>
    );
}

function LaborPoolsPanel() {
    const [pools, setPools] = useState({});
    const [busy, setBusy] = useState(null);
    async function load() {
        const { data } = await api.get("/admin/labor-pools");
        setPools(data.states || {});
    }
    useEffect(() => { load(); }, []);

    async function broadcast(state) {
        const subject = window.prompt(`Subject for the ${state} broadcast?`, "New shifts available in your area");
        if (!subject) return;
        const body = window.prompt("Message body?", "Hi — new shifts just posted in your area. Log in to claim.");
        if (!body) return;
        const channel = window.prompt("Channel? email / sms / both", "email");
        if (!channel) return;
        setBusy(state);
        try {
            const { data } = await api.post(`/admin/labor-pools/${state}/broadcast`, {
                subject, body, channel,
            });
            toast.success(`Sent to ${data.recipients} (email ${data.email_sent} · sms ${data.sms_sent})`);
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setBusy(null); }
    }
    async function recruit(state) {
        setBusy(state);
        try {
            const { data } = await api.post(`/admin/labor-pools/${state}/recruit`);
            toast.success(`Draft campaign created: "${data.name}"`);
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setBusy(null); }
    }

    const rows = Object.entries(pools).sort((a, b) => b[1].total - a[1].total);
    return (
        <div className="space-y-2">
            <div className="text-xs text-stone-500 font-dmsans mb-2">
                {rows.length} state pool{rows.length !== 1 ? "s" : ""} · click a row for actions
            </div>
            {rows.length === 0 && (
                <div className="text-sm text-stone-500 py-6 text-center font-dmsans">
                    No pools populated yet. Caregivers need a home state on their profile.
                </div>
            )}
            {rows.map(([state, p]) => (
                <div key={state} data-testid="ops-pool-row" data-state={state}
                     className="rounded-xl border border-stone-200 bg-white p-3 flex items-center gap-3 flex-wrap">
                    <div className="w-16 shrink-0">
                        <div className="font-outfit font-semibold text-2xl text-stone-900">{state}</div>
                    </div>
                    <div className="flex-1 min-w-[120px]">
                        <div className="text-xs text-stone-500 font-dmsans">Caregivers in pool</div>
                        <div className="font-outfit font-semibold text-lg text-emerald-700">
                            {p.total} <span className="text-xs text-stone-400 font-normal">total</span>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            data-testid="ops-pool-broadcast"
                            size="sm"
                            variant="outline"
                            disabled={busy === state}
                            onClick={() => broadcast(state)}
                            className="rounded-full h-8 text-xs"
                        >
                            <Send className="w-3 h-3 mr-1" /> Broadcast
                        </Button>
                        <Button
                            data-testid="ops-pool-recruit"
                            size="sm"
                            disabled={busy === state}
                            onClick={() => recruit(state)}
                            className="rounded-full h-8 text-xs bg-emerald-700 hover:bg-emerald-800 text-white"
                        >
                            <Sparkles className="w-3 h-3 mr-1" /> Boost pool
                        </Button>
                        <a
                            href={`/state/${state}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-stone-600 hover:text-emerald-700 font-dmsans inline-flex items-center px-2"
                        >
                            View public page <ExternalLink className="w-3 h-3 ml-1" />
                        </a>
                    </div>
                </div>
            ))}
        </div>
    );
}


// ---------------------------------------------------------------------------
// Apollo panel — LIVE Apollo.io lead search + saved pipeline
// ---------------------------------------------------------------------------
function ApolloPanel() {
    const [status, setStatus] = useState(null);
    const [mode, setMode] = useState("people"); // people | orgs | saved
    useEffect(() => { api.get("/admin/apollo/status").then((r) => setStatus(r.data)); }, []);

    return (
        <div className="space-y-4">
            <div className={`rounded-xl p-4 border ${status?.connected ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-1">
                            Apollo.io · {status?.org_label || "Infini HealthCare"}
                        </div>
                        <div className="font-outfit font-semibold text-stone-900 text-sm flex items-center gap-2">
                            {status?.connected
                                ? (<><CheckCircle2 className="w-4 h-4 text-emerald-700" /> Live · key {status.key_preview}</>)
                                : (<><AlertCircle className="w-4 h-4 text-amber-700" /> Not connected</>)}
                        </div>
                        {status?.note && (
                            <div className="text-xs text-stone-600 font-dmsans mt-1 max-w-xl">{status.note}</div>
                        )}
                    </div>
                </div>
            </div>
            {status?.connected && (
                <div className="flex gap-1 flex-wrap">
                    {[
                        { k: "people", label: "Decision-makers", icon: Users },
                        { k: "orgs", label: "Agencies", icon: Building2 },
                        { k: "saved", label: "Saved pipeline", icon: Star },
                    ].map((t) => {
                        const I = t.icon;
                        const active = mode === t.k;
                        return (
                            <button
                                key={t.k}
                                data-testid={`apollo-mode-${t.k}`}
                                onClick={() => setMode(t.k)}
                                className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[11px] font-outfit font-medium transition-all ${active ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`}
                            >
                                <I className="w-3.5 h-3.5" /> {t.label}
                            </button>
                        );
                    })}
                </div>
            )}
            {status?.connected && mode === "people" && <ApolloPeople />}
            {status?.connected && mode === "orgs" && <ApolloOrgs />}
            {status?.connected && mode === "saved" && <ApolloSaved />}
        </div>
    );
}

function ApolloPeople() {
    const [titles, setTitles] = useState("owner, director of nursing, administrator");
    const [state, setState] = useState("");
    const [keywords, setKeywords] = useState("home health");
    const [busy, setBusy] = useState(false);
    const [results, setResults] = useState([]);
    const [meta, setMeta] = useState(null);

    async function search() {
        setBusy(true);
        try {
            const person_locations = state
                ? [`${US_STATES.find(([c]) => c === state)?.[1] || state}, United States`]
                : [];
            const { data } = await api.post("/admin/apollo/search/people", {
                person_titles: titles.split(",").map((s) => s.trim()).filter(Boolean),
                person_locations,
                q_keywords: keywords || undefined,
                page: 1,
                per_page: 25,
            });
            setResults(data.results || []);
            setMeta({ total: data.total, credits_used: data.credits_used });
        } catch (e) {
            toast.error(e?.response?.data?.detail?.body?.error || e?.response?.data?.detail || "Apollo search failed");
        } finally {
            setBusy(false);
        }
    }

    async function save(p) {
        try {
            await api.post("/admin/apollo/leads", {
                kind: "person",
                apollo_id: p.apollo_id,
                name: p.name,
                email: p.email || undefined,
                phone: p.phone || undefined,
                title: p.title,
                org_name: p.org_name,
                org_domain: p.org_domain,
                linkedin_url: p.linkedin_url,
                city: p.city,
                state: p.state,
                country: p.country,
                industry: p.org_industry,
                employees: p.org_size,
            });
            toast.success(`Saved ${p.name}`);
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    }

    return (
        <div className="space-y-3">
            <div className="rounded-xl border border-stone-200 bg-white p-3 grid md:grid-cols-4 gap-2">
                <input
                    data-testid="apollo-people-titles"
                    value={titles}
                    onChange={(e) => setTitles(e.target.value)}
                    placeholder="Titles (comma separated)"
                    className="h-10 rounded-lg border border-stone-200 px-3 text-sm font-dmsans md:col-span-2"
                />
                <select
                    data-testid="apollo-people-state"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className="h-10 rounded-lg border border-stone-200 px-3 text-sm font-dmsans bg-white"
                >
                    <option value="">Any state</option>
                    {US_STATES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
                </select>
                <input
                    data-testid="apollo-people-keywords"
                    value={keywords}
                    onChange={(e) => setKeywords(e.target.value)}
                    placeholder="Keywords (e.g. home health)"
                    className="h-10 rounded-lg border border-stone-200 px-3 text-sm font-dmsans"
                />
                <Button
                    data-testid="apollo-people-search"
                    disabled={busy}
                    onClick={search}
                    className="rounded-full h-10 bg-stone-900 hover:bg-stone-800 text-white text-xs md:col-span-4"
                >
                    <Search className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Searching…" : "Search decision-makers"}
                </Button>
            </div>
            {meta && (
                <div className="text-[11px] text-stone-500 font-dmsans">
                    ~{meta.total?.toLocaleString?.() || meta.total} results in Apollo · showing {results.length}
                    {meta.credits_used ? ` · credits used this call: ${meta.credits_used}` : ""}
                </div>
            )}
            <div className="space-y-1 max-h-[440px] overflow-y-auto">
                {results.map((p) => (
                    <div key={p.apollo_id} data-testid="apollo-people-row" className="p-3 border border-stone-100 rounded-xl bg-white flex items-center justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                            <div className="font-outfit font-semibold text-stone-900 text-sm truncate">
                                {p.name} <span className="text-stone-500 font-normal">· {p.title || "—"}</span>
                            </div>
                            <div className="text-xs text-stone-500 font-dmsans truncate">
                                {p.org_name}{p.org_domain ? ` (${p.org_domain})` : ""} · {p.city ? `${p.city}, ` : ""}{p.state || p.country || "—"}
                            </div>
                            <div className="text-[11px] text-stone-400 font-dmsans truncate">
                                {p.email ? `✉ ${p.email}` : "email locked"}{p.email_status ? ` · ${p.email_status}` : ""} · {p.phone || "no phone"}
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                            {p.linkedin_url && (
                                <a href={p.linkedin_url} target="_blank" rel="noreferrer"
                                    className="text-[11px] text-stone-500 hover:text-emerald-700 inline-flex items-center">
                                    LinkedIn <ExternalLink className="w-3 h-3 ml-0.5" />
                                </a>
                            )}
                            <Button
                                data-testid="apollo-people-save"
                                size="sm"
                                onClick={() => save(p)}
                                className="rounded-full h-7 text-[11px] bg-emerald-700 hover:bg-emerald-800 text-white"
                            >
                                <Star className="w-3 h-3 mr-1" /> Save
                            </Button>
                        </div>
                    </div>
                ))}
                {results.length === 0 && !busy && (
                    <div className="text-sm text-stone-500 py-6 text-center font-dmsans">
                        No results yet — run a search above.
                    </div>
                )}
            </div>
        </div>
    );
}

function ApolloOrgs() {
    const [state, setState] = useState("");
    const [keywords, setKeywords] = useState("home healthcare");
    const [busy, setBusy] = useState(false);
    const [results, setResults] = useState([]);
    const [meta, setMeta] = useState(null);

    async function search() {
        setBusy(true);
        try {
            const organization_locations = state
                ? [`${US_STATES.find(([c]) => c === state)?.[1] || state}, United States`]
                : [];
            const { data } = await api.post("/admin/apollo/search/organizations", {
                organization_locations,
                q_keywords: keywords || undefined,
                page: 1,
                per_page: 25,
            });
            setResults(data.results || []);
            setMeta({ total: data.total });
        } catch (e) {
            toast.error(e?.response?.data?.detail?.body?.error || e?.response?.data?.detail || "Apollo search failed");
        } finally {
            setBusy(false);
        }
    }

    async function save(o) {
        try {
            await api.post("/admin/apollo/leads", {
                kind: "organization",
                apollo_id: o.apollo_id,
                name: o.name,
                phone: o.phone,
                org_name: o.name,
                org_domain: o.domain,
                linkedin_url: o.linkedin_url,
                city: o.city,
                state: o.state,
                country: o.country,
                industry: o.industry,
                employees: o.employees,
            });
            toast.success(`Saved ${o.name}`);
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    }

    return (
        <div className="space-y-3">
            <div className="rounded-xl border border-stone-200 bg-white p-3 grid md:grid-cols-3 gap-2">
                <select
                    data-testid="apollo-org-state"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className="h-10 rounded-lg border border-stone-200 px-3 text-sm font-dmsans bg-white"
                >
                    <option value="">Any state</option>
                    {US_STATES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
                </select>
                <input
                    data-testid="apollo-org-keywords"
                    value={keywords}
                    onChange={(e) => setKeywords(e.target.value)}
                    placeholder="Keywords"
                    className="h-10 rounded-lg border border-stone-200 px-3 text-sm font-dmsans"
                />
                <Button
                    data-testid="apollo-org-search"
                    disabled={busy}
                    onClick={search}
                    className="rounded-full h-10 bg-stone-900 hover:bg-stone-800 text-white text-xs"
                >
                    <Search className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Searching…" : "Find agencies"}
                </Button>
            </div>
            {meta && (
                <div className="text-[11px] text-stone-500 font-dmsans">
                    ~{meta.total?.toLocaleString?.() || meta.total} agencies in Apollo · showing {results.length}
                </div>
            )}
            <div className="space-y-1 max-h-[440px] overflow-y-auto">
                {results.map((o) => (
                    <div key={o.apollo_id} data-testid="apollo-org-row" className="p-3 border border-stone-100 rounded-xl bg-white flex items-center justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                            <div className="font-outfit font-semibold text-stone-900 text-sm truncate">
                                {o.name} <span className="text-stone-500 font-normal">· {o.industry || "—"}</span>
                            </div>
                            <div className="text-xs text-stone-500 font-dmsans truncate">
                                {o.domain || "no domain"} · {o.city ? `${o.city}, ` : ""}{o.state || o.country || "—"} · {o.employees || "?"} employees
                            </div>
                            <div className="text-[11px] text-stone-400 font-dmsans line-clamp-2">
                                {o.description || ""}
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                            {o.linkedin_url && (
                                <a href={o.linkedin_url} target="_blank" rel="noreferrer"
                                    className="text-[11px] text-stone-500 hover:text-emerald-700 inline-flex items-center">
                                    LinkedIn <ExternalLink className="w-3 h-3 ml-0.5" />
                                </a>
                            )}
                            <Button
                                data-testid="apollo-org-save"
                                size="sm"
                                onClick={() => save(o)}
                                className="rounded-full h-7 text-[11px] bg-emerald-700 hover:bg-emerald-800 text-white"
                            >
                                <Star className="w-3 h-3 mr-1" /> Save
                            </Button>
                        </div>
                    </div>
                ))}
                {results.length === 0 && !busy && (
                    <div className="text-sm text-stone-500 py-6 text-center font-dmsans">
                        No results yet — run a search above.
                    </div>
                )}
            </div>
        </div>
    );
}

function ApolloSaved() {
    const [leads, setLeads] = useState([]);
    const [filter, setFilter] = useState("");
    async function load() {
        const q = filter ? `?status=${filter}` : "";
        const { data } = await api.get(`/admin/apollo/leads${q}`);
        setLeads(data.leads || []);
    }
    useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

    async function updateStatus(id, next) {
        try {
            await api.patch(`/admin/apollo/leads/${id}`, { status: next });
            toast.success(`Moved to ${next}`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    }
    async function del(id) {
        if (!window.confirm("Remove this lead?")) return;
        try {
            await api.delete(`/admin/apollo/leads/${id}`);
            toast.success("Removed");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    }

    const STATUSES = ["new", "contacted", "responded", "booked", "dropped"];
    return (
        <div className="space-y-3">
            <div className="flex gap-1 flex-wrap items-center">
                <button
                    data-testid="apollo-saved-filter-all"
                    onClick={() => setFilter("")}
                    className={`px-3 h-7 rounded-full text-[11px] font-outfit ${!filter ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-700"}`}
                >
                    All ({leads.length})
                </button>
                {STATUSES.map((s) => (
                    <button
                        key={s}
                        data-testid={`apollo-saved-filter-${s}`}
                        onClick={() => setFilter(s)}
                        className={`px-3 h-7 rounded-full text-[11px] font-outfit capitalize ${filter === s ? "bg-emerald-700 text-white" : "bg-stone-100 text-stone-700"}`}
                    >
                        {s}
                    </button>
                ))}
            </div>
            <div className="space-y-1 max-h-[520px] overflow-y-auto">
                {leads.map((l) => (
                    <div key={l.id} data-testid="apollo-saved-row" className="p-3 border border-stone-100 rounded-xl bg-white flex items-center justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                            <div className="font-outfit font-semibold text-stone-900 text-sm truncate flex items-center gap-1.5">
                                <Badge className="bg-stone-100 text-stone-600 border-stone-200 hover:bg-stone-100 text-[9px] font-mono uppercase">{l.kind}</Badge>
                                {l.name}
                                {l.title && <span className="text-stone-500 font-normal"> · {l.title}</span>}
                            </div>
                            <div className="text-xs text-stone-500 font-dmsans truncate">
                                {l.org_name}{l.org_domain ? ` (${l.org_domain})` : ""} · {l.state || l.country || "—"} · {l.email || "no email"}
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <select
                                data-testid="apollo-saved-status"
                                value={l.status}
                                onChange={(e) => updateStatus(l.id, e.target.value)}
                                className="h-7 rounded-full border border-stone-200 bg-white px-2 text-[11px] font-dmsans capitalize"
                            >
                                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                            <button
                                data-testid="apollo-saved-delete"
                                onClick={() => del(l.id)}
                                className="text-stone-400 hover:text-red-700 p-1"
                                title="Remove"
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                ))}
                {leads.length === 0 && (
                    <div className="text-sm text-stone-500 py-6 text-center font-dmsans">
                        No saved leads yet — save some from Decision-makers or Agencies.
                    </div>
                )}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Broadcasts panel — mass-message open shifts + re-engagement KPIs
// ---------------------------------------------------------------------------
function BroadcastsPanel() {
    const [mode, setMode] = useState("compose"); // compose | history | kpi
    return (
        <div className="space-y-4">
            <div className="flex gap-1 flex-wrap">
                {[
                    { k: "compose", label: "New blast", icon: Zap },
                    { k: "history", label: "History", icon: Radio },
                    { k: "kpi", label: "Response KPIs", icon: Sparkles },
                ].map((t) => {
                    const I = t.icon;
                    const active = mode === t.k;
                    return (
                        <button
                            key={t.k}
                            data-testid={`broadcasts-mode-${t.k}`}
                            onClick={() => setMode(t.k)}
                            className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[11px] font-outfit font-medium transition-all ${active ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`}
                        >
                            <I className="w-3.5 h-3.5" /> {t.label}
                        </button>
                    );
                })}
            </div>
            {mode === "compose" && <BroadcastCompose />}
            {mode === "history" && <BroadcastHistory />}
            {mode === "kpi" && <BroadcastKpis />}
        </div>
    );
}

function BroadcastCompose() {
    const [form, setForm] = useState({
        title: "", address: "", zip: "", hourly_rate: "35",
        credentials: [], mode: "auto_first_yes",
        shift_start: "", shift_end: "", notes: "",
        collect_window_seconds: 300,
    });
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [gate, setGate] = useState(null); // { role, payment_method_on_file }
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    useEffect(() => {
        // Fetch the current user's profile to detect an agency without payment details
        api.get("/auth/me").then(({ data }) => {
            setGate({
                role: data.role,
                payment_method_on_file: !!data.payment_method_on_file,
                agency_name: data.agency_name || null,
                ein: data.ein || null,
            });
        }).catch(() => setGate(null));
    }, []);

    const gated =
        gate?.role === "agency" &&
        !gate?.payment_method_on_file;

    function toggleCred(c) {
        set("credentials", form.credentials.includes(c)
            ? form.credentials.filter((x) => x !== c)
            : [...form.credentials, c]);
    }

    function buildPayload(dryRun) {
        return {
            title: form.title,
            address: form.address,
            zip: form.zip,
            credentials: form.credentials,
            hourly_rate_cents: Math.round((parseFloat(form.hourly_rate) || 0) * 100),
            shift_start: form.shift_start || null,
            shift_end: form.shift_end || null,
            notes: form.notes || null,
            mode: form.mode,
            collect_window_seconds: parseInt(form.collect_window_seconds, 10) || 300,
            dry_run: !!dryRun,
        };
    }

    async function runPreview() {
        if (!/^\d{5}$/.test(form.zip)) { toast.error("ZIP must be 5 digits"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/broadcasts", buildPayload(true));
            setPreview(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Preview failed");
        } finally { setBusy(false); }
    }

    async function send() {
        if (!form.title || !form.address || !/^\d{5}$/.test(form.zip)) {
            toast.error("Title, address, and 5-digit ZIP are required");
            return;
        }
        if (!window.confirm(`Send blast to ${preview?.matched_caregivers ?? "?"} caregivers?`)) return;
        setBusy(true);
        try {
            const { data } = await api.post("/broadcasts", buildPayload(false));
            setResult(data);
            toast.success(`Blast queued — ${data.sent + data.mocked} delivered, ${data.failed} failed`);
        } catch (e) {
            // Surface the specific "payment method required" gate error nicely
            const detail = e?.response?.data?.detail;
            if (detail && typeof detail === "object" && detail.code === "PAYMENT_METHOD_REQUIRED") {
                toast.error(detail.message);
                if (detail.hint_url) {
                    setTimeout(() => window.location.assign(detail.hint_url), 1200);
                }
            } else {
                toast.error(typeof detail === "string" ? detail : "Send failed");
            }
        } finally { setBusy(false); }
    }

    return (
        <div className="rounded-2xl border border-stone-200 bg-white p-5">
            {gated && (
                <div
                    data-testid="broadcast-payment-gate"
                    className="mb-4 rounded-xl border-2 border-amber-200 bg-amber-50 p-4 flex items-start gap-3"
                >
                    <AlertCircle className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="font-outfit font-semibold text-sm text-amber-900">
                            Add your payment details before posting a shift
                        </div>
                        <div className="text-xs text-amber-800 font-dmsans mt-0.5">
                            {gate?.agency_name || "Your agency"} needs a card or ACH bank account on file so we can settle platform fees and route payouts. This takes about 60 seconds.
                        </div>
                    </div>
                    <a
                        href="/payments"
                        data-testid="broadcast-payment-gate-cta"
                        className="shrink-0 inline-flex items-center px-4 h-9 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-outfit font-semibold"
                    >
                        Go to Payment Settings
                    </a>
                </div>
            )}
            <div className={gated ? "opacity-60 pointer-events-none" : ""}>
            <div className="grid md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                    <Label>Shift title</Label>
                    <Input
                        data-testid="broadcast-title"
                        value={form.title}
                        onChange={(e) => set("title", e.target.value)}
                        placeholder="Overnight home care · 8pm–8am"
                        className="mt-1 h-11 rounded-xl"
                    />
                </div>
                <div>
                    <Label>Address</Label>
                    <Input
                        data-testid="broadcast-address"
                        value={form.address}
                        onChange={(e) => set("address", e.target.value)}
                        placeholder="123 Elm St, Los Angeles CA"
                        className="mt-1 h-11 rounded-xl"
                    />
                </div>
                <div>
                    <Label>ZIP</Label>
                    <Input
                        data-testid="broadcast-zip"
                        value={form.zip}
                        onChange={(e) => set("zip", e.target.value.replace(/[^0-9]/g, "").slice(0, 5))}
                        placeholder="90210"
                        inputMode="numeric"
                        className="mt-1 h-11 rounded-xl"
                    />
                </div>
                <div>
                    <Label>Your total hourly budget ($)</Label>
                    <Input
                        data-testid="broadcast-rate"
                        value={form.hourly_rate}
                        onChange={(e) => set("hourly_rate", e.target.value.replace(/[^0-9.]/g, ""))}
                        placeholder="50"
                        className="mt-1 h-11 rounded-xl"
                    />
                    {parseFloat(form.hourly_rate) > 0 && (
                        <div className="mt-2 rounded-lg border border-stone-200 bg-stone-50 p-2.5 text-[11px] font-dmsans" data-testid="broadcast-rate-split">
                            <div className="flex items-center justify-between text-stone-600">
                                <span>Platform staffing fee (35%)</span>
                                <span className="font-mono font-semibold text-stone-900">
                                    ${(parseFloat(form.hourly_rate) * 0.35).toFixed(2)}/hr
                                </span>
                            </div>
                            <div className="flex items-center justify-between mt-1 text-emerald-800">
                                <span>Caregiver receives (65%)</span>
                                <span className="font-mono font-semibold">
                                    ${(parseFloat(form.hourly_rate) * 0.65).toFixed(2)}/hr
                                </span>
                            </div>
                            <div className="mt-1.5 text-[10px] text-stone-400">
                                Caregivers only see their take-home rate in the email.
                            </div>
                        </div>
                    )}
                </div>
                <div className="md:col-span-2">
                    <Label>Credentials (leave empty for anyone)</Label>
                    <div className="mt-1 flex gap-1 flex-wrap">
                        {["RN", "LPN", "CNA", "HHA", "OTHER"].map((c) => (
                            <button
                                key={c}
                                type="button"
                                data-testid={`broadcast-cred-${c.toLowerCase()}`}
                                onClick={() => toggleCred(c)}
                                className={`px-3 h-8 rounded-full text-[11px] font-outfit font-medium border-2 transition-all ${form.credentials.includes(c) ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-stone-700 border-stone-200 hover:border-stone-300"}`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </div>
                <div>
                    <Label>Start</Label>
                    <Input
                        type="datetime-local"
                        data-testid="broadcast-start"
                        value={form.shift_start}
                        onChange={(e) => set("shift_start", e.target.value)}
                        className="mt-1 h-11 rounded-xl"
                    />
                </div>
                <div>
                    <Label>End</Label>
                    <Input
                        type="datetime-local"
                        data-testid="broadcast-end"
                        value={form.shift_end}
                        onChange={(e) => set("shift_end", e.target.value)}
                        className="mt-1 h-11 rounded-xl"
                    />
                </div>
                <div className="md:col-span-2">
                    <Label>Notes (optional)</Label>
                    <Input
                        data-testid="broadcast-notes"
                        value={form.notes}
                        onChange={(e) => set("notes", e.target.value)}
                        placeholder="Hoyer lift required · Non-smoker preferred"
                        className="mt-1 h-11 rounded-xl"
                    />
                </div>
                <div className="md:col-span-2">
                    <Label>Assignment mode</Label>
                    <div className="mt-1 grid sm:grid-cols-3 gap-2">
                        {[
                            { k: "auto_first_yes", label: "First Yes wins", desc: "Best for regular shifts" },
                            { k: "collect_5min", label: "5-min collection", desc: "Best for hospital sitter / urgent — admin picks" },
                            { k: "collect_open", label: "Open collection", desc: "Admin picks anytime" },
                        ].map((m) => (
                            <button
                                key={m.k}
                                type="button"
                                data-testid={`broadcast-assignment-${m.k}`}
                                onClick={() => set("mode", m.k)}
                                className={`rounded-xl border-2 p-3 text-left transition-all ${form.mode === m.k ? "border-emerald-700 bg-emerald-50" : "border-stone-200 bg-white hover:border-stone-300"}`}
                            >
                                <div className="font-outfit font-semibold text-sm text-stone-900">{m.label}</div>
                                <div className="text-[11px] text-stone-500 font-dmsans mt-0.5">{m.desc}</div>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            <div className="mt-5 flex gap-2 flex-wrap">
                <Button
                    data-testid="broadcast-preview"
                    disabled={busy}
                    onClick={runPreview}
                    variant="outline"
                    className="rounded-full h-10 text-xs"
                >
                    <Search className="w-3.5 h-3.5 mr-1.5" /> Preview audience
                </Button>
                <Button
                    data-testid="broadcast-send"
                    disabled={busy || !preview}
                    onClick={send}
                    className="rounded-full h-10 bg-emerald-700 hover:bg-emerald-800 text-white text-xs"
                >
                    <Send className="w-3.5 h-3.5 mr-1.5" />
                    {busy ? "Sending…" : `Send blast${preview ? ` to ${preview.matched_caregivers}` : ""}`}
                </Button>
            </div>
            {preview && !result && (
                <div className="mt-4 rounded-xl border border-stone-200 bg-stone-50 p-3 text-xs font-dmsans text-stone-700 space-y-1.5" data-testid="broadcast-preview-result">
                    <div className="flex items-center gap-2 font-semibold">
                        <Users className="w-4 h-4" /> {preview.matched_caregivers} caregivers match
                    </div>
                    {preview.sample_emails?.length ? (
                        <div className="text-stone-500 text-[11px]">Sample: {preview.sample_emails.join(" · ")}</div>
                    ) : (
                        <div className="text-amber-700">No caregivers cover ZIP {form.zip} yet. Ask them to add {form.zip} under &quot;Manage my ZIPs&quot;.</div>
                    )}
                    {typeof preview.estimated_cost_cents === "number" && preview.matched_caregivers > 0 && (
                        <div className="flex items-center justify-between pt-1.5 border-t border-stone-200">
                            <div className="text-stone-600">Estimated Resend cost</div>
                            <div className="font-mono font-semibold text-stone-900">
                                ~${(preview.estimated_cost_cents / 100).toFixed(2)}
                            </div>
                        </div>
                    )}
                    {preview.warmup && (
                        <div className={`flex items-center justify-between text-[11px] rounded-lg px-2 py-1.5 mt-1 ${preview.warmup.would_exceed_cap ? "bg-amber-100 text-amber-900" : "bg-white text-stone-600 border border-stone-200"}`}>
                            <span>
                                Sender warm-up:{" "}
                                {preview.warmup.daily_cap === null
                                    ? "unlimited (fully warmed)"
                                    : `${preview.warmup.sent_last_24h} of ${preview.warmup.daily_cap} sent in last 24h`}
                            </span>
                            {preview.warmup.would_exceed_cap && (
                                <span className="font-semibold">⚠ Cap will pause remainder</span>
                            )}
                        </div>
                    )}
                </div>
            )}
            {result && (
                <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs font-dmsans text-emerald-900" data-testid="broadcast-result">
                    <div className="flex items-center gap-2 font-semibold mb-1">
                        <CheckCircle2 className="w-4 h-4" /> Blast sent
                    </div>
                    Targeted: <strong>{result.targeted}</strong> · Delivered: <strong>{result.sent}</strong> · Mocked: <strong>{result.mocked}</strong> · Failed: <strong>{result.failed}</strong>
                    {result.deferred > 0 && <> · <strong>Deferred (warm-up cap)</strong>: {result.deferred}</>}
                </div>
            )}
            </div>
        </div>
    );
}

function BroadcastHistory() {
    const [items, setItems] = useState([]);
    const [detail, setDetail] = useState(null);
    async function load() {
        const { data } = await api.get("/broadcasts");
        setItems(data.broadcasts || []);
    }
    async function openDetail(id) {
        const { data } = await api.get(`/broadcasts/broadcast-detail/${id}`);
        setDetail(data);
    }
    useEffect(() => { load(); }, []);
    return (
        <div className="grid md:grid-cols-[320px,1fr] gap-3">
            <div className="space-y-1 max-h-[560px] overflow-y-auto">
                {items.length === 0 && (
                    <div className="text-sm text-stone-500 py-6 text-center font-dmsans">No blasts yet.</div>
                )}
                {items.map((b) => (
                    <button
                        key={b.id}
                        data-testid="broadcast-history-row"
                        onClick={() => openDetail(b.id)}
                        className={`w-full text-left p-3 border rounded-xl transition-all ${detail?.broadcast?.id === b.id ? "border-emerald-700 bg-emerald-50" : "border-stone-100 bg-white hover:border-stone-300"}`}
                    >
                        <div className="flex items-center justify-between mb-1">
                            <Badge className={`text-[9px] uppercase ${b.status === "filled" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : b.status === "open" ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-stone-100 text-stone-600 border-stone-200"} hover:bg-opacity-100`}>
                                {b.status}
                            </Badge>
                            <div className="text-[10px] text-stone-500 font-mono">ZIP {b.zip}</div>
                        </div>
                        <div className="font-outfit font-semibold text-sm text-stone-900 truncate">{b.title}</div>
                        <div className="text-[11px] text-stone-500 font-dmsans truncate mt-0.5">
                            ${((b.caregiver_rate_cents ?? b.hourly_rate_cents * 0.65) / 100).toFixed(0)}/hr to caregiver · {b.total_targeted} sent · {b.yes_count} ✓ · {b.no_count} ✗
                        </div>
                    </button>
                ))}
            </div>
            <div className="min-w-0">
                {detail ? <BroadcastDetail data={detail} /> : (
                    <div className="text-sm text-stone-500 font-dmsans p-6 text-center border border-dashed border-stone-200 rounded-xl">
                        Select a blast to see recipient breakdown.
                    </div>
                )}
            </div>
        </div>
    );
}

function BroadcastDetail({ data }) {
    const b = data.broadcast;
    const c = data.counts;
    return (
        <div className="border border-stone-200 rounded-xl bg-white p-4" data-testid="broadcast-detail">
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">ZIP {b.zip} · {b.mode}</div>
                    <h3 className="font-outfit font-semibold text-lg text-stone-900">{b.title}</h3>
                    <div className="text-xs text-stone-500 font-dmsans">
                        {b.address} · ${(b.hourly_rate_cents / 100).toFixed(2)}/hr
                    </div>
                </div>
                <Badge className={`${b.status === "filled" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-amber-100 text-amber-700 border-amber-200"}`}>
                    {b.status}
                </Badge>
            </div>
            <div className="grid grid-cols-4 gap-2 mb-4">
                <div className="rounded-xl bg-stone-50 p-3 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-stone-500 font-outfit">Total</div>
                    <div className="text-xl font-outfit font-bold text-stone-900">{c.total}</div>
                </div>
                <div className="rounded-xl bg-emerald-50 p-3 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-outfit">Yes</div>
                    <div className="text-xl font-outfit font-bold text-emerald-800">{c.yes}</div>
                </div>
                <div className="rounded-xl bg-stone-50 p-3 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-stone-500 font-outfit">No</div>
                    <div className="text-xl font-outfit font-bold text-stone-800">{c.no}</div>
                </div>
                <div className="rounded-xl bg-amber-50 p-3 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-amber-700 font-outfit">Pending</div>
                    <div className="text-xl font-outfit font-bold text-amber-800">{c.pending}</div>
                </div>
            </div>
            <div className="space-y-1 max-h-[360px] overflow-y-auto">
                {data.recipients.map((r) => (
                    <div key={r.id} className="flex items-center justify-between gap-2 p-2 border border-stone-100 rounded-lg text-xs font-dmsans">
                        <div className="min-w-0 flex-1 truncate">
                            <span className="font-semibold text-stone-800">{r.caregiver_name || r.caregiver_email}</span>
                            <span className="text-stone-400"> · {r.zip || "—"}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            {r.won && <Badge className="bg-emerald-700 hover:bg-emerald-700 text-white text-[9px]">WINNER</Badge>}
                            <Badge className={`text-[9px] ${r.response === "yes" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : r.response === "no" ? "bg-stone-100 text-stone-600 border-stone-200" : "bg-amber-100 text-amber-700 border-amber-200"}`}>
                                {r.response || "no response"}
                            </Badge>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function BroadcastKpis() {
    const [days, setDays] = useState(30);
    const [k, setK] = useState(null);
    const [busy, setBusy] = useState(false);
    const [reengaging, setReengaging] = useState(false);

    async function load() {
        setBusy(true);
        try {
            const { data } = await api.get(`/broadcasts/kpis/overview?days=${days}`);
            setK(data);
        } catch (e) { toast.error(e?.response?.data?.detail || "KPI fetch failed"); }
        finally { setBusy(false); }
    }
    useEffect(() => { load(); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps

    async function runReengage(dryRun) {
        setReengaging(true);
        try {
            const { data } = await api.post("/broadcasts/reengage-nonresponders",
                { days_silent: days, dry_run: dryRun });
            if (dryRun) {
                toast.success(`${data.would_reengage} caregivers would be re-engaged`);
            } else {
                toast.success(`Re-engagement sent — ${data.sent + data.mocked} delivered / ${data.failed} failed`);
                load();
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Re-engagement failed");
        } finally { setReengaging(false); }
    }

    async function reactivateSnoozed() {
        try {
            const { data } = await api.post("/broadcasts/reactivate-snoozed", {});
            toast.success(`${data.reactivated} caregivers reactivated`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Reactivate failed"); }
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
                <Label className="text-xs font-outfit font-medium text-stone-600 uppercase tracking-wider">Window</Label>
                {[7, 30, 60, 90].map((d) => (
                    <button
                        key={d}
                        data-testid={`broadcast-kpi-window-${d}`}
                        onClick={() => setDays(d)}
                        className={`px-3 h-7 rounded-full text-[11px] font-outfit ${days === d ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-700"}`}
                    >
                        {d}d
                    </button>
                ))}
                <div className="ml-auto flex gap-1.5">
                    <Button
                        data-testid="broadcast-reengage-preview"
                        onClick={() => runReengage(true)}
                        variant="outline"
                        disabled={reengaging}
                        size="sm"
                        className="rounded-full h-8 text-[11px]"
                    >
                        <Search className="w-3 h-3 mr-1" /> Preview re-engage
                    </Button>
                    <Button
                        data-testid="broadcast-reengage-send"
                        onClick={() => runReengage(false)}
                        disabled={reengaging}
                        size="sm"
                        className="rounded-full h-8 text-[11px] bg-emerald-700 hover:bg-emerald-800 text-white"
                    >
                        <Send className="w-3 h-3 mr-1" /> Send re-engage
                    </Button>
                    <Button
                        data-testid="broadcast-reactivate-snoozed"
                        onClick={reactivateSnoozed}
                        variant="outline"
                        size="sm"
                        className="rounded-full h-8 text-[11px]"
                    >
                        <PauseCircle className="w-3 h-3 mr-1" /> Reactivate snoozed
                    </Button>
                </div>
            </div>
            {busy && <div className="text-sm text-stone-500 font-dmsans">Loading KPIs…</div>}
            {k && (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                        <BKpi label="Sent" value={k.total_sent} tone="stone" />
                        <BKpi label="Yes" value={k.total_yes} tone="emerald" />
                        <BKpi label="No" value={k.total_no} tone="stone" />
                        <BKpi label="No response" value={k.total_non_responders} tone="amber" />
                        <BKpi label="Response rate" value={`${k.response_rate_pct}%`} tone="emerald" />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        <BKpi label="Currently snoozed" value={k.snoozed_caregivers} tone="amber" small />
                        <BKpi label="Opted out" value={k.opted_out_caregivers} tone="stone" small />
                        <BKpi label="Window" value={`${k.window_days} days`} tone="stone" small />
                    </div>
                    <div>
                        <div className="text-[10px] uppercase font-outfit font-bold tracking-[0.2em] text-stone-500 mb-2">
                            Response rate by ZIP
                        </div>
                        <div className="border border-stone-200 rounded-xl overflow-hidden bg-white">
                            <table className="w-full text-xs font-dmsans" data-testid="broadcast-kpi-zip-table">
                                <thead className="bg-stone-50 text-[10px] uppercase text-stone-500 font-outfit">
                                    <tr>
                                        <th className="text-left p-2">ZIP</th>
                                        <th className="text-right p-2">Sent</th>
                                        <th className="text-right p-2">Yes</th>
                                        <th className="text-right p-2">No</th>
                                        <th className="text-right p-2">No reply</th>
                                        <th className="text-right p-2">Pool size</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {k.by_zip.length === 0 && (
                                        <tr><td colSpan={6} className="p-4 text-center text-stone-500">
                                            No blasts in this window yet.
                                        </td></tr>
                                    )}
                                    {k.by_zip.map((r) => {
                                        const nonReplies = r.sent - r.yes - r.no;
                                        return (
                                            <tr key={r.zip} className="border-t border-stone-100">
                                                <td className="p-2 font-mono">{r.zip}</td>
                                                <td className="p-2 text-right">{r.sent}</td>
                                                <td className="p-2 text-right text-emerald-700 font-semibold">{r.yes}</td>
                                                <td className="p-2 text-right text-stone-600">{r.no}</td>
                                                <td className="p-2 text-right text-amber-700">{nonReplies}</td>
                                                <td className="p-2 text-right text-stone-800 font-semibold">{r.pool_size}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

function BKpi({ label, value, tone = "stone", small = false }) {
    const tones = {
        stone: "bg-stone-50 border-stone-200 text-stone-900",
        emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
        amber: "bg-amber-50 border-amber-200 text-amber-900",
    };
    return (
        <div className={`rounded-xl border p-3 ${tones[tone]}`}>
            <div className="text-[10px] uppercase font-outfit font-bold tracking-[0.15em] opacity-70">
                {label}
            </div>
            <div className={`font-outfit font-bold mt-1 ${small ? "text-lg" : "text-2xl"}`}>{value}</div>
        </div>
    );
}


// ---------------------------------------------------------------------------
// Referrals panel — leaderboard + pending Amazon gift-card claims
// ---------------------------------------------------------------------------
function ReferralsPanel() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    async function load() {
        try {
            const { data } = await api.get("/admin/referrals/board");
            setData(data);
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    }
    useEffect(() => { load(); }, []);
    async function fulfill(claimId) {
        setBusy(true);
        try {
            await api.post(`/admin/referrals/claims/${claimId}/fulfill`);
            toast.success("Marked fulfilled — remember to email the caregiver their Amazon gift card");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Fulfill failed");
        } finally { setBusy(false); }
    }
    if (!data) return <div className="text-sm text-stone-500 font-dmsans py-4">Loading…</div>;
    return (
        <div className="space-y-5">
            {/* Pending claims first — these need action */}
            <div>
                <div className="text-[10px] uppercase font-outfit font-bold tracking-[0.2em] text-stone-500 mb-2 flex items-center gap-1.5">
                    <Gift className="w-3.5 h-3.5" /> Pending Amazon gift-card claims ({data.pending_claims.length})
                </div>
                {data.pending_claims.length === 0 ? (
                    <div className="text-sm text-stone-500 font-dmsans p-4 border border-dashed border-stone-200 rounded-xl">
                        Nothing pending. Everyone with 5 refs + 80h has been paid out.
                    </div>
                ) : (
                    <div className="space-y-2">
                        {data.pending_claims.map((c) => (
                            <div key={c.id} data-testid="referral-pending-row" className="rounded-xl border-2 border-amber-200 bg-amber-50 p-3 flex items-center justify-between gap-3 flex-wrap">
                                <div className="min-w-0 flex-1">
                                    <div className="font-outfit font-semibold text-sm text-stone-900 truncate">
                                        {c.caregiver_name} · ${c.amount_usd} Amazon
                                    </div>
                                    <div className="text-xs text-stone-600 font-dmsans truncate">
                                        {c.caregiver_email} · code {c.referral_code} · {c.referral_count} refs · {c.total_hours}h · claimed {c.created_at?.slice(0,10)}
                                    </div>
                                </div>
                                <div className="flex gap-1.5">
                                    <a
                                        href={`mailto:${c.caregiver_email}?subject=Your%20%24${c.amount_usd}%20Amazon%20gift%20card&body=Hi%20${encodeURIComponent(c.caregiver_name||'there')}%2C%0A%0AThanks%20for%20being%20one%20of%20our%20top%20referrers!%20Here%27s%20your%20%24${c.amount_usd}%20Amazon%20gift%20card%3A%0A%0A%5BPASTE%20GIFT%20CARD%20CODE%20HERE%5D`}
                                        className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-white border border-stone-200 hover:border-emerald-600 text-xs font-outfit font-medium text-stone-800"
                                    >
                                        <Mail className="w-3 h-3" /> Email
                                    </a>
                                    <button
                                        data-testid="referral-fulfill"
                                        onClick={() => fulfill(c.id)}
                                        disabled={busy}
                                        className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-outfit font-semibold"
                                    >
                                        <CheckCircle2 className="w-3 h-3" /> Mark fulfilled
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {/* Leaderboard */}
            <div>
                <div className="text-[10px] uppercase font-outfit font-bold tracking-[0.2em] text-stone-500 mb-2">
                    Leaderboard · top referrers
                </div>
                {data.leaderboard.length === 0 ? (
                    <div className="text-sm text-stone-500 font-dmsans p-4 border border-dashed border-stone-200 rounded-xl">
                        No one has referred anyone yet.
                    </div>
                ) : (
                    <div className="border border-stone-200 rounded-xl overflow-hidden bg-white">
                        <table className="w-full text-xs font-dmsans" data-testid="referral-leaderboard">
                            <thead className="bg-stone-50 text-[10px] uppercase text-stone-500 font-outfit">
                                <tr>
                                    <th className="text-left p-2">Caregiver</th>
                                    <th className="text-left p-2">Code</th>
                                    <th className="text-right p-2">Refs</th>
                                    <th className="text-right p-2">Hours</th>
                                    <th className="text-right p-2">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.leaderboard.map((r) => (
                                    <tr key={r.id} className="border-t border-stone-100">
                                        <td className="p-2">
                                            <div className="font-semibold text-stone-900">{r.name || "—"}</div>
                                            <div className="text-stone-500 text-[10px]">{r.email}</div>
                                        </td>
                                        <td className="p-2 font-mono text-emerald-700">{r.referral_code}</td>
                                        <td className="p-2 text-right font-semibold text-stone-900">{r.referrals}</td>
                                        <td className="p-2 text-right text-stone-600">{r.hours}</td>
                                        <td className="p-2 text-right">
                                            {r.claimed ? (
                                                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[9px]">CLAIMED</Badge>
                                            ) : r.eligible ? (
                                                <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[9px]">ELIGIBLE</Badge>
                                            ) : (
                                                <Badge className="bg-stone-100 text-stone-600 border-stone-200 text-[9px]">IN PROGRESS</Badge>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

